import PropTypes from 'prop-types';
import MessageBubble from './MessageBubble';
import { useRoomContext } from '../../../../../context/RoomContext';
import { formatFileSize } from '../../../../../utils/fileUtils';
import { getThumbnailBlobUrl } from '../../../../../hooks/useMediaThumbnail';
import './MessageGroup.css';

const MessageGroup = ({
  senderId = '',
  messages = [],
  name = '',
  avatar = '',
  shield = null,
  isUser = false,
  onDeleteMessage = (_messageId) => {},
  onReplyMessage = (_message, _senderName, _content) => {},
  onEditMessage = (_message, _content) => {},
  editHighlightedMessageId = null,
  selectedMessageId = null,
  allMessageGroups = [], // New prop to access all messages for reply lookup
  onReactionDetailsOpen = () => {},
  onMessageDetailsOpen = () => {},
  onFocusMessage = () => {},
  onContactClick = () => {},
  onEditHistoryOpen = () => {},
}) => {
  const { readReceipts } = useRoomContext();
  const isUserClass = (isUser) ? 'user' : '';

  // Helper function to check if a message is a reply
  const isReplyMessage = (message) => {
    const content = message.getContent();
    return content && content['m.relates_to'] && content['m.relates_to']['m.in_reply_to'];
  };

  // Helper function to get the original message that was replied to
  const getOriginalReplyMessage = (replyMessage) => {
    const content = replyMessage.getContent();
    const replyToEventId = content['m.relates_to']?.['m.in_reply_to']?.event_id;
    
    if (!replyToEventId) return null;
    
    // Search through all message groups to find the original message
    for (const messageGroup of allMessageGroups) {
      for (const msg of messageGroup.messages) {
        if (msg.getId() === replyToEventId) {
          const originalMsgContent = msg.getClearContent?.() || msg.getContent();
          const msgtype = originalMsgContent.msgtype;
          const isAttachment = ['m.image', 'm.video', 'm.audio', 'm.file'].includes(msgtype);
          const filename = isAttachment ? originalMsgContent.filename || originalMsgContent.body : null;
          const mimeType = isAttachment ? originalMsgContent.info?.mimetype : null;
          const size = isAttachment ? formatFileSize(originalMsgContent.info?.size) : null;
          
          // Use the pre-generated blob URL if it exists on the message object.
          const thumbnailUrl = getThumbnailBlobUrl(msg);
          return {
            eventId: replyToEventId,
            // For attachments, use filename as content, otherwise use body.
            content: isAttachment ? filename : originalMsgContent.body,
            sender: messageGroup.name,
            senderIsUser: messageGroup.isUser,
            isAttachment,
            thumbnailUrl,
            // Pass attachment info for icon rendering
            msgtype,
            filename,
            mimeType,
            size,
          };
        }
      }
    }
    
    return {
      eventId: replyToEventId,
      content: '[Original message not found]',
      sender: 'Unknown',
      senderIsUser: false,
    };
  };

  const handleAvatarClick = () => {
    if (onContactClick) {
      onContactClick(senderId);
    }
  };

  const renderMessages = () => (
    <div className="message-group-wrapper">
      {
        messages.map((message, index) => {
          const isReply = isReplyMessage(message);
          const isEdited = message._isEdited || false;
          const originalReplyMessage = isReply ? getOriginalReplyMessage(message) : null;

          return (
            <MessageBubble
              key={message.getId()}
              message={message}
              name={name}
              isUser={isUser}
              shield={shield}
              isFirstInGroup={index === 0}
              isRead={(readReceipts.get(message.getId())?.size || 0) > 0}
              isLastInGroup={index === messages.length - 1}
              onDeleteMessage={onDeleteMessage}
              onReplyMessage={onReplyMessage}
              onEditMessage={onEditMessage}
              isEditHighlighted={editHighlightedMessageId === message.getId()}
              isSelected={selectedMessageId === message.getId()}
              isReply={isReply}
              isEdited={isEdited}
              originalReplyMessage={originalReplyMessage}
              onReactionDetailsOpen={onReactionDetailsOpen}
              onFocusMessage={onFocusMessage}
              onMessageDetailsOpen={onMessageDetailsOpen}
              onEditHistoryOpen={onEditHistoryOpen}
            />
          );
        })
      }
    </div>
  );

  return (
    <div className={`message-group-container ${isUserClass}`}>
      <div className={`message-group ${isUserClass}`}>
        {
          (!isUser) && (
            <div className="message-group-avatar">
              <img
                src={avatar}
                alt={name}
                onClick={handleAvatarClick}
                crossOrigin="anonymous"
              />
            </div>
          )
        }
        {renderMessages()}
      </div>
    </div>
  );
};

MessageGroup.propTypes = {
  senderId: PropTypes.string,
  messages: PropTypes.array.isRequired,
  name: PropTypes.string.isRequired,
  avatar: PropTypes.string.isRequired,
  shield: PropTypes.object,
  isUser: PropTypes.bool.isRequired,
  onDeleteMessage: PropTypes.func,
  onReplyMessage: PropTypes.func,
  onEditMessage: PropTypes.func,
  editHighlightedMessageId: PropTypes.string,
  selectedMessageId: PropTypes.string,
  allMessageGroups: PropTypes.array,
  onReactionDetailsOpen: PropTypes.func,
  onMessageDetailsOpen: PropTypes.func,
  onFocusMessage: PropTypes.func,
  onContactClick: PropTypes.func,
  onEditHistoryOpen: PropTypes.func, // <-- Add this line
};

export default MessageGroup;
